import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip
} from '@mui/material';
import dayjs from 'dayjs';
import { CalendarEvent, Listing } from '../../schemas/calendar';
import { getColorByType } from '../../utils/StatusColors';

interface MonthViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  listings: Listing[];
  onEventClick: (event: CalendarEvent) => void;
  onSlotClick: (start: Date, end: Date) => void;
}

const MonthView: React.FC<MonthViewProps> = ({
  currentDate,
  events,
  listings,
  onEventClick,
  onSlotClick
}) => {
  const monthStart = dayjs(currentDate).startOf('month');
  const monthEnd = dayjs(currentDate).endOf('month');
  const calendarStart = monthStart.startOf('week');
  const calendarEnd = monthEnd.endOf('week');
  
  const calendarDays = [];
  let day = calendarStart;
  
  while (day.isBefore(calendarEnd) || day.isSame(calendarEnd, 'day')) {
    calendarDays.push(day);
    day = day.add(1, 'day');
  }

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getEventsForDay = (day: dayjs.Dayjs) => {
    return events.filter(event => 
      dayjs(event.startTime).isSame(day, 'day')
    );
  };

  const getEventColor = (event: CalendarEvent) => {
    const listing = listings.find(l => l.id === event.listingId);
    switch (event.status) {
      case 'confirmed':
        return listing?.color || '#7c3aed';
      case 'pending':
        return '#f59e0b';
      case 'cancelled':
        return '#6b7280';
      default:
        return listing?.color || '#7c3aed';
    }
  };

  const isToday = (day: dayjs.Dayjs) => day.isSame(dayjs(), 'day');
  const isCurrentMonth = (day: dayjs.Dayjs) => day.isSame(dayjs(currentDate), 'month');

  return (
    <Box sx={{ height: '100%', overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <Box sx={{ minWidth: { xs: 560, md: '100%' } }}>
      {/* Header with Days of Week */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(80px, 1fr))',
          borderBottom: '2px solid',
          borderColor: 'grey.200',
          position: 'sticky',
          top: 0,
          backgroundColor: 'background.default',
          zIndex: 10
        }}
      >
        {weekDays.map((dayName) => (
          <Box
            key={dayName}
            sx={{
              p: { xs: 1, md: 2 },
              borderRight: '1px solid',
              borderColor: 'grey.200',
              textAlign: 'center',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="primary"
              sx={{ fontSize: { xs: '0.7rem', md: '1.25rem' } }}
            >
              {dayName}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Calendar Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(80px, 1fr))',
          gridTemplateRows: `repeat(${Math.ceil(calendarDays.length / 7)}, 1fr)`,
          height: 'calc(100% - 80px)'
        }}
      >
        {calendarDays.map((day) => {
          const dayEvents = getEventsForDay(day);
          const maxVisibleEvents = 3;
          const hiddenEventsCount = Math.max(0, dayEvents.length - maxVisibleEvents);
          
          return (
            <Box
              key={day.format('YYYY-MM-DD')}
              sx={{
                borderRight: '1px solid',
                borderColor: 'grey.200',
                borderBottom: '1px solid',
                p: 1,
                cursor: 'pointer',
                backgroundColor: isToday(day) 
                  ? 'rgba(124, 58, 237, 0.1)' 
                  : 'transparent',
                opacity: isCurrentMonth(day) ? 1 : 0.5,
                '&:hover': {
                  backgroundColor: isToday(day) 
                    ? 'rgba(124, 58, 237, 0.2)' 
                    : 'grey.50'
                },
                transition: 'all 0.3s ease',
                minHeight: { xs: 140, md: 160 },
                overflow: 'hidden'
              }}
              onClick={() => {
                const start = day.hour(9).minute(0).second(0).toDate();
                const end = day.hour(10).minute(0).second(0).toDate();
                onSlotClick(start, end);
              }}
            >
              {/* Day Number */}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: isToday(day) ? 700 : 600,
                  color: isToday(day) ? 'primary.main' : 'inherit',
                  mb: 1
                }}
              >
                {day.format('D')}
              </Typography>

              {/* Events */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {dayEvents.slice(0, maxVisibleEvents).map((event) => {
                  const listing = listings.find(l => l.id === event.listingId);
                  return (
                    <Paper
                      key={event.id}
                      elevation={1}
                      sx={{
                        // backgroundColor: getEventColor(event),
                        backgroundColor: getColorByType(listing!),
                        px: 1,
                        py: 0.5,
                        cursor: 'pointer',
                        borderRadius: 2,
                        '&:hover': {
                          transform: 'scale(1.02)',
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                        },
                        transition: 'all 0.3s ease',
                        overflow: 'hidden'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEventClick(event);
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          display: 'block',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontSize: '0.7rem'
                        }}
                      >
                        {dayjs(event.startTime).format('h:mm A')} {event.title}
                      </Typography>
                      {listing && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            opacity: 0.9,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontSize: '0.6rem'
                          }}
                        >
                          {listing.name}
                        </Typography>
                      )}
                      {event.status !== 'confirmed' && (
                        <Chip
                          label={event.status}
                          size="small"
                          sx={{
                            height: 12,
                            fontSize: '0.5rem',
                            backgroundColor: 'rgba(255,255,255,0.3)',
                            mt: 0.2
                          }}
                        />
                      )}
                    </Paper>
                  );
                })}
                
                {hiddenEventsCount > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 600,
                      fontSize: '0.6rem',
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Could open a popover or dialog showing all events
                    }}
                  >
                    +{hiddenEventsCount} more
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
      </Box>
    </Box>
  );
};

export default MonthView;


// Better UI Month View

// import React, { useState } from 'react';
// import {
//   Box,
//   Typography,
//   Paper,
//   Chip,
//   useTheme,
//   alpha,
//   Badge,
//   Tooltip,
//   IconButton,
//   Stack,
//   Popover,
//   List,
//   ListItem,
//   ListItemText,
//   Divider
// } from '@mui/material';
// import {
//   Add,
//   Person,
//   Schedule,
//   LocationOn,
//   FiberManualRecord,
//   MoreHoriz,
//   CalendarMonth
// } from '@mui/icons-material';
// import dayjs from 'dayjs';
// import { CalendarEvent, Listing } from '../../schemas/calendar';
// import { getColorByType } from '../../utils/StatusColors';

// interface MonthViewProps {
//   currentDate: Date;
//   events: CalendarEvent[];
//   listings: Listing[];
//   onEventClick: (event: CalendarEvent) => void;
//   onSlotClick: (start: Date, end: Date) => void;
// }

// const MonthView: React.FC<MonthViewProps> = ({
//   currentDate,
//   events,
//   listings,
//   onEventClick,
//   onSlotClick
// }) => {
//   const theme = useTheme();
//   const [hoveredDay, setHoveredDay] = useState<string | null>(null);
//   const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
//   const [popoverEvents, setPopoverEvents] = useState<CalendarEvent[]>([]);

//   const monthStart = dayjs(currentDate).startOf('month');
//   const monthEnd = dayjs(currentDate).endOf('month');
//   const calendarStart = monthStart.startOf('week');
//   const calendarEnd = monthEnd.endOf('week');
  
//   const calendarDays = [];
//   let day = calendarStart;
  
//   while (day.isBefore(calendarEnd) || day.isSame(calendarEnd, 'day')) {
//     calendarDays.push(day);
//     day = day.add(1, 'day');
//   }

//   const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
//   const weekDaysShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

//   const getEventsForDay = (day: dayjs.Dayjs) => {
//     return events.filter(event => 
//       dayjs(event.startTime).isSame(day, 'day')
//     ).sort((a, b) => dayjs(a.startTime).diff(dayjs(b.startTime)));
//   };

//   const getStatusColor = (status: string) => {
//     switch (status) {
//       case 'confirmed': return theme.palette.success.main;
//       case 'pending': return theme.palette.warning.main;
//       case 'cancelled': return theme.palette.error.main;
//       default: return theme.palette.grey[500];
//     }
//   };

//   const isToday = (day: dayjs.Dayjs) => day.isSame(dayjs(), 'day');
//   const isCurrentMonth = (day: dayjs.Dayjs) => day.isSame(dayjs(currentDate), 'month');
//   const isWeekend = (day: dayjs.Dayjs) => day.day() === 0 || day.day() === 6;

//   const handleMoreEventsClick = (e: React.MouseEvent<HTMLElement>, events: CalendarEvent[]) => {
//     e.stopPropagation();
//     setPopoverAnchor(e.currentTarget);
//     setPopoverEvents(events);
//   };

//   const EventChip = ({ event, isCompact = false }: { event: CalendarEvent; isCompact?: boolean }) => {
//     const listing = listings.find(l => l.id === event.listingId);
//     const eventColor = getColorByType(listing!);
    
//     return (
//       <Tooltip
//         title={
//           <Box sx={{ p: 1 }}>
//             <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
//               {event.title}
//             </Typography>
//             <Stack spacing={0.5}>
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                 <Schedule sx={{ fontSize: 12 }} />
//                 <Typography variant="caption">
//                   {dayjs(event.startTime).format('h:mm A')} - {dayjs(event.endTime).format('h:mm A')}
//                 </Typography>
//               </Box>
//               {event.customerName && (
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                   <Person sx={{ fontSize: 12 }} />
//                   <Typography variant="caption">{event.customerName}</Typography>
//                 </Box>
//               )}
//               {listing && (
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                   <LocationOn sx={{ fontSize: 12 }} />
//                   <Typography variant="caption">{listing.name}</Typography>
//                 </Box>
//               )}
//             </Stack>
//           </Box>
//         }
//         placement="top"
//         arrow
//       >
//         <Paper
//           elevation={0}
//           sx={{
//             background: `linear-gradient(135deg, ${eventColor} 0%, ${alpha(eventColor, 0.8)} 100%)`,
//             color: theme.palette.getContrastText(eventColor),
//             px: isCompact ? 0.5 : 1,
//             py: isCompact ? 0.25 : 0.5,
//             cursor: 'pointer',
//             borderRadius: 1.5,
//             border: `1px solid ${alpha(eventColor, 0.3)}`,
//             transition: 'all 0.2s ease-in-out',
//             position: 'relative',
//             '&:hover': {
//               transform: 'translateY(-1px)',
//               boxShadow: `0 4px 12px ${alpha(eventColor, 0.3)}`,
//               zIndex: 2
//             },
//             '&::before': {
//               content: '""',
//               position: 'absolute',
//               left: 0,
//               top: 0,
//               bottom: 0,
//               width: '3px',
//               backgroundColor: getStatusColor(event.status),
//               borderRadius: '1.5px 0 0 1.5px'
//             }
//           }}
//           onClick={(e) => {
//             e.stopPropagation();
//             onEventClick(event);
//           }}
//         >
//           <Box sx={{ pl: 0.5 }}>
//             <Typography
//               variant="caption"
//               sx={{
//                 fontWeight: 600,
//                 display: 'block',
//                 whiteSpace: 'nowrap',
//                 overflow: 'hidden',
//                 textOverflow: 'ellipsis',
//                 fontSize: isCompact ? '0.65rem' : '0.75rem',
//                 lineHeight: 1.2
//               }}
//             >
//               {dayjs(event.startTime).format('h:mm A')} {event.title}
//             </Typography>
//             {!isCompact && listing && (
//               <Typography
//                 variant="caption"
//                 sx={{
//                   display: 'block',
//                   opacity: 0.85,
//                   whiteSpace: 'nowrap',
//                   overflow: 'hidden',
//                   textOverflow: 'ellipsis',
//                   fontSize: '0.65rem',
//                   lineHeight: 1.1
//                 }}
//               >
//                 {listing.name}
//               </Typography>
//             )}
//           </Box>
//         </Paper>
//       </Tooltip>
//     );
//   };

//   const DayCell = ({ day }: { day: dayjs.Dayjs }) => {
//     const dayEvents = getEventsForDay(day);
//     const dayKey = day.format('YYYY-MM-DD');
//     const maxVisibleEvents = 3;
//     const hiddenEvents = dayEvents.slice(maxVisibleEvents);
//     const isHovered = hoveredDay === dayKey;
//     const todayCell = isToday(day);
//     const currentMonthCell = isCurrentMonth(day);
//     const weekendCell = isWeekend(day);

//     return (
//       <Box
//         key={dayKey}
//         sx={{
//           border: `1px solid ${theme.palette.divider}`,
//           p: 1.5,
//           cursor: 'pointer',
//           minHeight: { xs: 100, sm: 120, md: 140 },
//           backgroundColor: todayCell 
//             ? alpha(theme.palette.primary.main, 0.08)
//             : weekendCell && currentMonthCell
//             ? alpha(theme.palette.grey[100], 0.5)
//             : 'transparent',
//           opacity: currentMonthCell ? 1 : 0.4,
//           transition: 'all 0.2s ease-in-out',
//           position: 'relative',
//           overflow: 'hidden',
//           '&:hover': {
//             backgroundColor: todayCell 
//               ? alpha(theme.palette.primary.main, 0.12)
//               : alpha(theme.palette.primary.main, 0.04),
//             transform: 'scale(1.02)',
//             zIndex: 1,
//             boxShadow: theme.shadows[4]
//           },
//           '&::before': todayCell ? {
//             content: '""',
//             position: 'absolute',
//             top: 0,
//             left: 0,
//             right: 0,
//             height: '3px',
//             backgroundColor: theme.palette.primary.main,
//             borderRadius: '0 0 3px 3px'
//           } : {}
//         }}
//         onMouseEnter={() => setHoveredDay(dayKey)}
//         onMouseLeave={() => setHoveredDay(null)}
//         onClick={() => {
//           const start = day.hour(9).minute(0).second(0).toDate();
//           const end = day.hour(10).minute(0).second(0).toDate();
//           onSlotClick(start, end);
//         }}
//       >
//         {/* Day Number and Add Button */}
//         <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
//           <Typography
//             variant="h6"
//             sx={{
//               fontWeight: todayCell ? 800 : currentMonthCell ? 600 : 400,
//               color: todayCell 
//                 ? theme.palette.primary.main
//                 : currentMonthCell 
//                 ? theme.palette.text.primary
//                 : theme.palette.text.disabled,
//               fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
//               lineHeight: 1
//             }}
//           >
//             {day.format('D')}
//           </Typography>

//           {/* Add Event Button on Hover */}
//           {isHovered && currentMonthCell && (
//             <IconButton
//               size="small"
//               sx={{
//                 backgroundColor: alpha(theme.palette.primary.main, 0.1),
//                 color: theme.palette.primary.main,
//                 width: 24,
//                 height: 24,
//                 '&:hover': {
//                   backgroundColor: alpha(theme.palette.primary.main, 0.2)
//                 }
//               }}
//               onClick={(e) => {
//                 e.stopPropagation();
//                 const start = day.hour(9).minute(0).second(0).toDate();
//                 const end = day.hour(10).minute(0).second(0).toDate();
//                 onSlotClick(start, end);
//               }}
//             >
//               <Add fontSize="small" />
//             </IconButton>
//           )}

//           {/* Event Count Badge */}
//           {dayEvents.length > 0 && (
//             <Badge
//               badgeContent={dayEvents.length}
//               color="primary"
//               sx={{
//                 '& .MuiBadge-badge': {
//                   fontSize: '0.6rem',
//                   minWidth: 16,
//                   height: 16,
//                   borderRadius: '50%'
//                 }
//               }}
//             >
//               <CalendarMonth 
//                 sx={{ 
//                   fontSize: 16, 
//                   color: alpha(theme.palette.primary.main, 0.7)
//                 }} 
//               />
//             </Badge>
//           )}
//         </Box>

//         {/* Events */}
//         <Stack spacing={0.5} sx={{ flexGrow: 1 }}>
//           {dayEvents.slice(0, maxVisibleEvents).map((event) => (
//             <EventChip key={event.id} event={event} />
//           ))}
          
//           {hiddenEvents.length > 0 && (
//             <Box
//               sx={{
//                 display: 'flex',
//                 alignItems: 'center',
//                 cursor: 'pointer',
//                 p: 0.5,
//                 borderRadius: 1,
//                 backgroundColor: alpha(theme.palette.grey[300], 0.5),
//                 '&:hover': {
//                   backgroundColor: alpha(theme.palette.grey[400], 0.7)
//                 }
//               }}
//               onClick={(e) => handleMoreEventsClick(e, hiddenEvents)}
//             >
//               <MoreHoriz sx={{ fontSize: 14, mr: 0.5, color: theme.palette.text.secondary }} />
//               <Typography
//                 variant="caption"
//                 sx={{
//                   color: theme.palette.text.secondary,
//                   fontWeight: 600,
//                   fontSize: '0.65rem'
//                 }}
//               >
//                 +{hiddenEvents.length} more
//               </Typography>
//             </Box>
//           )}
//         </Stack>
//       </Box>
//     );
//   };

//   return (
//     <Box sx={{ 
//       height: '100%', 
//       overflow: 'auto',
//       backgroundColor: theme.palette.background.default
//     }}>
//       {/* Enhanced Header */}
//       <Paper
//         elevation={2}
//         sx={{
//           display: 'grid',
//           gridTemplateColumns: 'repeat(7, 1fr)',
//           position: 'sticky',
//           top: 0,
//           zIndex: 10,
//           backgroundColor: theme.palette.background.paper,
//           borderRadius: 0,
//           borderBottom: `3px solid ${theme.palette.primary.main}`
//         }}
//       >
//         {weekDays.map((dayName, index) => (
//           <Box
//             key={dayName}
//             sx={{
//               p: { xs: 1.5, sm: 2 },
//               borderRight: index < 6 ? `1px solid ${theme.palette.divider}` : 'none',
//               textAlign: 'center',
//               background: index === 0 || index === 6 
//                 ? `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`
//                 : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
//               borderBottom: `2px solid ${index === 0 || index === 6 ? theme.palette.secondary.main : theme.palette.primary.main}`
//             }}
//           >
//             <Typography 
//               variant="h6" 
//               fontWeight={700} 
//               color={index === 0 || index === 6 ? 'secondary.main' : 'primary.main'}
//               sx={{
//                 fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
//                 display: { xs: 'none', sm: 'block' }
//               }}
//             >
//               {dayName}
//             </Typography>
//             <Typography 
//               variant="h6" 
//               fontWeight={700} 
//               color={index === 0 || index === 6 ? 'secondary.main' : 'primary.main'}
//               sx={{
//                 fontSize: '0.9rem',
//                 display: { xs: 'block', sm: 'none' }
//               }}
//             >
//               {weekDaysShort[index]}
//             </Typography>
//           </Box>
//         ))}
//       </Paper>

//       {/* Calendar Grid */}
//       <Box
//         sx={{
//           display: 'grid',
//           gridTemplateColumns: 'repeat(7, 1fr)',
//           backgroundColor: theme.palette.background.paper
//         }}
//       >
//         {calendarDays.map((day) => (
//           <DayCell key={day.format('YYYY-MM-DD')} day={day} />
//         ))}
//       </Box>

//       {/* More Events Popover */}
//       <Popover
//         open={Boolean(popoverAnchor)}
//         anchorEl={popoverAnchor}
//         onClose={() => setPopoverAnchor(null)}
//         anchorOrigin={{
//           vertical: 'bottom',
//           horizontal: 'left',
//         }}
//         transformOrigin={{
//           vertical: 'top',
//           horizontal: 'left',
//         }}
//         PaperProps={{
//           sx: { 
//             maxWidth: 300,
//             borderRadius: 2,
//             border: `1px solid ${theme.palette.divider}`
//           }
//         }}
//       >
//         <Box sx={{ p: 2 }}>
//           <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
//             Additional Events
//           </Typography>
//           <List dense sx={{ py: 0 }}>
//             {popoverEvents.map((event, index) => {
//               const listing = listings.find(l => l.id === event.listingId);
//               return (
//                 <React.Fragment key={event.id}>
//                   {index > 0 && <Divider />}
//                   <ListItem
//                     sx={{ 
//                       px: 0,
//                       cursor: 'pointer',
//                       borderRadius: 1,
//                       '&:hover': {
//                         backgroundColor: alpha(theme.palette.primary.main, 0.05)
//                       }
//                     }}
//                     onClick={() => {
//                       onEventClick(event);
//                       setPopoverAnchor(null);
//                     }}
//                   >
//                     <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
//                       <FiberManualRecord 
//                         sx={{ 
//                           fontSize: 8, 
//                           color: getStatusColor(event.status)
//                         }} 
//                       />
//                       <Box sx={{ flexGrow: 1, minWidth: 0 }}>
//                         <Typography variant="body2" fontWeight={600} noWrap>
//                           {event.title}
//                         </Typography>
//                         <Typography variant="caption" color="text.secondary" noWrap>
//                           {dayjs(event.startTime).format('h:mm A')} - {listing?.name}
//                         </Typography>
//                       </Box>
//                     </Box>
//                   </ListItem>
//                 </React.Fragment>
//               );
//             })}
//           </List>
//         </Box>
//       </Popover>
//     </Box>
//   );
// };

// export default MonthView;